import React, { useState, useRef } from 'react';
import API from '../utils/api';

/**
 * LeaseUpload — drag-and-drop or click-to-upload a tenancy agreement.
 * On success, calls onExtracted(fields) with the pre-filled form values.
 */
export default function LeaseUpload({ onExtracted }) {
  const [status, setStatus] = useState('idle'); // idle | uploading | success | error
  const [message, setMessage] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();

  const processFile = async (file) => {
    if (!file) return;

    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setStatus('error');
      setMessage('Please upload a PDF, JPG or PNG file.');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setStatus('error');
      setMessage('File is too large. Maximum size is 20 MB.');
      return;
    }

    setStatus('uploading');
    setMessage('');

    const formData = new FormData();
    formData.append('lease', file);

    try {
      const { data } = await API.post('/extract-lease', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setStatus('success');

      // Build a friendly summary of what was found
      const found = [];
      if (data.propertyAddress) found.push('address');
      if (data.tenantName)      found.push('tenant name');
      if (data.tenantEmail)     found.push('email');
      if (data.fortnightlyRent) found.push('rent amount');
      if (data.leaseStartDate)  found.push('lease start date');

      const rentNote = data.rentFrequency && data.rentFrequency !== 'fortnightly'
        ? ` (converted from ${data.rentFrequency} to fortnightly)`
        : '';
      setMessage(`Found: ${found.join(', ')}${rentNote}. Review the details below before saving.`);

      onExtracted(data);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.error || 'Could not read the document. Try a clearer scan or a text-based PDF.');
    }
  };

  const handleFileInput = (e) => processFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-slate-700">Auto-fill from lease agreement</span>
        <span className="text-xs text-slate-400">optional</span>
      </div>

      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all px-5 py-6 text-center
          ${dragOver
            ? 'border-brand-400 bg-brand-50'
            : status === 'success'
              ? 'border-brand-300 bg-brand-50'
              : status === 'error'
                ? 'border-amber-300 bg-amber-50'
                : 'border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-brand-50'
          }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={handleFileInput}
          className="hidden"
        />

        {status === 'uploading' ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
            <p className="text-sm text-brand-700 font-medium">Reading your lease agreement...</p>
            <p className="text-xs text-slate-500">This usually takes 10–20 seconds</p>
          </div>
        ) : status === 'success' ? (
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-9 h-9 bg-brand-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-brand-800">Details extracted</p>
            <p className="text-xs text-brand-700 leading-relaxed max-w-sm">{message}</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setStatus('idle'); setMessage(''); }}
              className="text-xs text-slate-500 hover:underline mt-1"
            >
              Upload a different file
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">
                Drop your lease here, or <span className="text-brand-600">click to browse</span>
              </p>
              <p className="text-xs text-slate-400 mt-0.5">PDF, JPG or PNG · up to 20 MB</p>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {status === 'error' && (
        <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-xs text-amber-800">{message}</p>
        </div>
      )}
    </div>
  );
}
